// Copyright 2025-2026 Florian Gallon. All Rights reserved.
// Enhanced routing utility for multi-tenant bakery application
// Supports both old and new URL structures without breaking existing functionalities

import { db } from '../firebase';
import { collection, doc, getDoc, setDoc, query, where, getDocs } from 'firebase/firestore';

// Enhanced routing detection with support for:
// 0. mybakery.com (custom domain) - HIGHEST PRIORITY
// 1. name.digi-bakery.com (subdomain)
// 2. digi-bakery.com/namebakery (path-based)
// 3. digi-bakery.com/shop?bakeryId=xxx (legacy support)
export const getCurrentBakeryContext = async () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);

  // Method 0: Custom domain detection (mybakery.com) - HIGHEST PRIORITY
  const customDomainBakery = await detectCustomDomain(hostname);
  if (customDomainBakery) {
    return {
      bakeryId: customDomainBakery.id,
      bakery: customDomainBakery,
      routingMethod: 'customDomain',
      isCustomerFacing: true
    };
  }

  // Method 1: Subdomain detection (name.digi-bakery.com)
  const subdomainBakery = await detectSubdomainBakery(hostname);
  if (subdomainBakery) {
    return {
      bakeryId: subdomainBakery.id,
      bakery: subdomainBakery,
      routingMethod: 'subdomain',
      isCustomerFacing: true
    };
  }

  // Method 2: Path-based detection (digi-bakery.com/namebakery)
  const pathBakery = await detectPathBasedBakery(pathname);
  if (pathBakery) {
    return {
      bakeryId: pathBakery.id,
      bakery: pathBakery,
      routingMethod: 'path',
      isCustomerFacing: true
    };
  }

  // Method 3: Legacy URL parameter support (digi-bakery.com/shop?bakeryId=xxx)
  const legacyBakeryId = urlParams.get('bakeryId');
  if (legacyBakeryId) {
    const bakery = await getBakery(legacyBakeryId);
    if (bakery) {
      return {
        bakeryId: legacyBakeryId,
        bakery: bakery,
        routingMethod: 'legacy',
        isCustomerFacing: true
      };
    }
  }

  // Method 4: Check for localhost development with hash parameters
  if (hostname === 'localhost' || hostname.includes('localhost')) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const devBakeryId = hashParams.get('bakeryId');
    if (devBakeryId) {
      const bakery = await getBakery(devBakeryId);
      if (bakery) {
        return {
          bakeryId: devBakeryId,
          bakery: bakery,
          routingMethod: 'development',
          isCustomerFacing: true
        };
      }
    }
  }

  // No bakery context detected - this is the main commercial site
  return {
    bakeryId: null,
    bakery: null,
    routingMethod: 'main',
    isCustomerFacing: false
  };
};

// Detect bakery from subdomain (e.g., marysbakery.digi-bakery.com)
const detectSubdomainBakery = async (hostname) => {
  const parts = hostname.split('.');
  
  // Check if this looks like a subdomain pattern
  if (parts.length >= 3 && parts[1] === 'digi-bakery' && parts[2] === 'com') {
    const subdomain = parts[0];
    
    try {
      const bakery = await getBakeryBySubdomain(subdomain);
      if (bakery) {
        return bakery;
      }
    } catch (error) {
      console.error('Error checking subdomain:', error);
    }
  }
  
  return null;
};

// Detect bakery from path (e.g., digi-bakery.com/marysbakery)
const detectPathBasedBakery = async (pathname) => {
  // Extract the first path segment
  const pathSegments = pathname.split('/').filter(segment => segment.length > 0);

  if (pathSegments.length > 0) {
    const potentialSubdomain = pathSegments[0];

    try {
      const bakery = await getBakeryBySubdomain(potentialSubdomain);
      if (bakery) {
        return bakery;
      }
    } catch (error) {
      console.error('Error checking path-based routing:', error);
    }
  }

  return null;
};

// Detect bakery from custom domain (e.g., mybakery.com)
const detectCustomDomain = async (hostname) => {
  // Skip if it's our main domain or localhost
  if (
    hostname === 'localhost' ||
    hostname.includes('localhost') ||
    hostname.includes('digi-bakery.com') ||
    hostname.includes('vercel.app')
  ) {
    return null;
  }

  // Remove 'www.' prefix if present for lookup
  const cleanHostname = hostname.replace(/^www\./, '');

  try {
    const bakery = await getBakeryByCustomDomain(cleanHostname);
    if (bakery) {
      return bakery;
    }
  } catch (error) {
    console.error('Error checking custom domain:', error);
  }

  return null;
};

// Get bakery by custom domain
export const getBakeryByCustomDomain = async (domain) => {
  try {
    const q = query(
      collection(db, 'bakeries'),
      where('customDomain', '==', domain),
      where('customDomainVerified', '==', true)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting bakery by custom domain:', error);
    return null;
  }
};

// Get bakery by ID
export const getBakery = async (bakeryId) => {
  try {
    const bakeryDoc = await getDoc(doc(db, 'bakeries', bakeryId));
    if (bakeryDoc.exists()) {
      return { id: bakeryDoc.id, ...bakeryDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting bakery:', error);
    throw error;
  }
};

// Get bakery by subdomain
export const getBakeryBySubdomain = async (subdomain) => {
  try {
    const q = query(collection(db, 'bakeries'), where('subdomain', '==', subdomain));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting bakery by subdomain:', error);
    throw error;
  }
};

// Generate unique subdomain for new bakeries
export const generateUniqueSubdomain = async (baseName) => {
  try {
    // Clean the base name to make it URL-friendly
    let cleanName = baseName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') // Remove non-alphanumeric characters
      .substring(0, 20); // Limit length
    
    if (!cleanName) {
      cleanName = 'bakery'; // Fallback if name becomes empty
    }
    
    // Check if the base name is available
    let subdomain = cleanName;
    let counter = 1;
    
    while (await getBakeryBySubdomain(subdomain)) {
      subdomain = `${cleanName}${counter}`;
      counter++;
      
      // Prevent infinite loop
      if (counter > 1000) {
        subdomain = `${cleanName}${Date.now()}`;
        break;
      }
    }
    
    return subdomain;
  } catch (error) {
    console.error('Error generating unique subdomain:', error);
    throw error;
  }
};

// Update bakery settings
export const updateBakery = async (bakeryId, updates) => {
  try {
    const bakeryRef = doc(db, 'bakeries', bakeryId);
    await setDoc(bakeryRef, updates, { merge: true });
  } catch (error) {
    console.error('Error updating bakery:', error);
    throw error;
  }
};

// Get the appropriate view based on routing context
export const getInitialView = (bakeryContext, userProfile) => {
  // If we're on the main site (no bakery context)
  if (!bakeryContext.bakeryId) {
    return 'commercial-landing';
  }

  // If we're on a bakery-specific URL
  if (bakeryContext.isCustomerFacing) {
    // For logged-in bakers on their own bakery
    if (userProfile?.role === 'baker' && userProfile.bakeryId === bakeryContext.bakeryId) {
      return 'baker-orders';
    }

    // For customers or visitors, show the bakery's home page first
    return 'home';
  }

  // Default fallback
  return 'home';
};

// Generate customer-friendly URLs for bakeries
export const generateBakeryURL = (bakery, preferredMethod = 'subdomain') => {
  const baseURL = process.env.REACT_APP_BASE_URL || 'https://digi-bakery.com';

  // Prefer custom domain if available and verified
  if (bakery.customDomain && bakery.customDomainVerified) {
    return `https://${bakery.customDomain}`;
  }

  if (preferredMethod === 'subdomain' && bakery.subdomain) {
    return `https://${bakery.subdomain}.digi-bakery.com`;
  } else if (bakery.subdomain) {
    return `${baseURL}/${bakery.subdomain}`;
  } else {
    // Fallback to legacy format
    return `${baseURL}/shop?bakeryId=${bakery.id}`;
  }
};

// Check if current URL needs to be redirected to a better format
export const shouldRedirectToFriendlyURL = (bakeryContext) => {
  // Don't redirect for search engine bots - let them index the current URL
  const userAgent = navigator.userAgent.toLowerCase();
  const isCrawler = /bot|crawler|spider|crawling|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|whatsapp|twitterbot|linkedinbot/i.test(userAgent);

  if (isCrawler) {
    return false; // Let crawlers index the current URL without redirect
  }

  // Redirect legacy URLs for regular users
  if (bakeryContext.routingMethod === 'legacy') {
    return true;
  }

  return false;
};

// Check if we should redirect to custom domain (for SEO consolidation)
export const shouldRedirectToCustomDomain = (bakeryContext) => {
  // Don't redirect for search engine bots
  const userAgent = navigator.userAgent.toLowerCase();
  const isCrawler = /bot|crawler|spider|crawling|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|whatsapp|twitterbot|linkedinbot/i.test(userAgent);

  if (isCrawler) {
    return false;
  }

  // Only redirect if we're on subdomain or path-based routing AND bakery has a verified custom domain
  if (
    (bakeryContext.routingMethod === 'subdomain' || bakeryContext.routingMethod === 'path') &&
    bakeryContext.bakery?.customDomain &&
    bakeryContext.bakery?.customDomainVerified
  ) {
    return true;
  }

  return false;
};

// Redirect to custom domain
export const redirectToCustomDomain = (bakery) => {
  const pathname = window.location.pathname;
  const search = window.location.search;

  // Build the custom domain URL, preserving the path after the bakery subdomain
  // e.g., /marysbakery/shop → mybakery.com/shop
  let newPath = pathname;

  // If it's path-based routing, remove the bakery subdomain from the path
  if (bakery.subdomain && pathname.startsWith(`/${bakery.subdomain}`)) {
    newPath = pathname.replace(`/${bakery.subdomain}`, '') || '/';
  }

  const customDomainURL = `https://${bakery.customDomain}${newPath}${search}`;
  window.location.replace(customDomainURL);
};

// Redirect to friendly URL if needed
export const redirectToFriendlyURL = (bakery) => {
  const friendlyURL = generateBakeryURL(bakery, 'path'); // Use path-based for better compatibility
  window.location.replace(friendlyURL);
};